import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Sun, Moon } from "lucide-react";
import { IDS } from "@/lib/ids";
import { useTheme } from "@/lib/theme";

const links = [
  { id: "mechanism", label: "Mechanism", href: "/#mechanism" },
  { id: "product", label: "Product", href: "/#product" },
  { id: "frameworks", label: "Frameworks", href: "/#frameworks" },
  { id: "trust", label: "Trust", href: "/#trust" },
  { id: "pricing", label: "Pricing", href: "/#pricing" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { theme, toggle } = useTheme();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <motion.header
      data-testid={IDS.nav}
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-40 transition-[background,backdrop-filter,border,color] duration-300 ${scrolled ? "backdrop-blur-md hairline-b" : ""}`}
      style={{ background: scrolled ? "color-mix(in oklab, var(--bg) 85%, transparent)" : "transparent", borderBottomColor: scrolled ? "var(--hairline)" : "transparent" }}
    >
      <div
        className="mx-auto max-w-[1400px] px-5 md:px-8 lg:px-10 h-[64px] md:h-[68px] flex items-center justify-between"
        style={{ gap: "clamp(0.75rem, 3vw, 2.5rem)" }}
      >
        <Link to="/" data-testid={IDS.navLogo} className="flex items-center gap-2.5 group shrink-0 transition-transform duration-300 group-hover:-translate-y-[1px]" aria-label="NormShift home" style={{ color: "var(--text)" }}>
          <svg viewBox="61.7 91.6 210.99 133.3" className="h-[26px] md:h-[30px] w-auto shrink-0" role="img" aria-label="NormShift logo">
            <path d="M223.5,91.6L223.5,91.6c0,0.1-0.1,0.1-0.1,0.2c-1.2,1.9-11,17.6-10.6,30.2l-2.7,3.4 c-3.1,3.9-7.9,6.2-12.9,6.2h-32l27-33.8c3.1-3.9,7.9-6.2,12.9-6.2H223.5z" fill="#e82532" />
            <path d="M105.3,168.5c-4.8-6-4.8-14.6,0-20.6l45-56.3h-32c-5,0-9.7,2.3-12.9,6.2l-40.1,50.1 c-4.8,6-4.8,14.6,0,20.6l34.8,43.5l19.8-24.8c-0.2-0.5-0.5-1-0.8-1.5L105.3,168.5z" fill="currentColor" />
            <path d="M194.3,144.4l-59,73.7c-3.1,3.9-7.9,6.2-12.9,6.2h-4.3c-2.4,0-4.8-0.5-7-1.5c-2.5-1.1-4.7-2.8-6.4-4.9 l-4.6-5.8l19.8-24.8l29.4-36.8c3.1-3.9,7.9-6.2,12.9-6.2H194.3z" fill="#e82532" />
            <path d="M269.1,168.5L229,218.7c-3.1,3.9-7.9,6.2-12.9,6.2h-32l45-56.3c4.8-6,4.8-14.6,0-20.6l-14-17.5 c-1.6-2.5-2.3-5.4-2.4-8.5c-0.4-12.6,9.4-28.3,10.6-30.2c0,0,0.1-0.1,0.1-0.1l0,0h0.6l45,56.3 C273.9,154,273.9,162.5,269.1,168.5z" fill="currentColor" />
            <path d="M212.8,122c0,0-1.6-9.4,10.7-30.3c0,0-20.2,17.3-20.2,38.7C203.3,130.4,207.5,129.4,212.8,122z" fill="#c72235" />
            <path d="M100.2,212.1c0,0,18.1-21.3,5.1-43.5l13.9,17.3c0,0,0.6,0.8,0.8,1.5L100.2,212.1z" fill="#c3c3c3" />
            <path d="M135.3,218.1c0,0,27-33.5,27-73.7h32L135.3,218.1z" fill="#c72235" />
          </svg>
          <svg viewBox="2.78 8.02 143.27 23.82" className="h-[16px] md:h-[18px] w-auto" aria-hidden>
            <path d="M4.38 8.66L4.38 8.66Q4.80 8.66 5 8.76Q5.20 8.86 5.46 9.18L5.46 9.18L18.74 26.46Q18.69 26.05 18.67 25.66Q18.66 25.26 18.66 24.90L18.66 24.90L18.66 8.66L21.38 8.66L21.38 31.58L19.81 31.58Q19.44 31.58 19.19 31.46Q18.94 31.33 18.70 31.02L18.70 31.02L5.44 13.76Q5.47 14.16 5.49 14.54Q5.50 14.93 5.50 15.25L5.50 15.25L5.50 31.58L2.78 31.58L2.78 8.66L4.38 8.66ZM33.09 15.12L33.09 15.12Q34.86 15.12 36.30 15.71Q37.73 16.30 38.73 17.39Q39.73 18.48 40.26 20.02Q40.80 21.57 40.80 23.47L40.80 23.47Q40.80 25.39 40.26 26.93Q39.73 28.46 38.73 29.55Q37.73 30.64 36.30 31.22Q34.86 31.81 33.09 31.81L33.09 31.81Q31.31 31.81 29.88 31.22Q28.45 30.64 27.44 29.55Q26.43 28.46 25.89 26.93Q25.34 25.39 25.34 23.47L25.34 23.47Q25.34 21.57 25.89 20.02Q26.43 18.48 27.44 17.39Q28.45 16.30 29.88 15.71Q31.31 15.12 33.09 15.12ZM33.09 29.58L33.09 29.58Q35.49 29.58 36.67 27.98Q37.86 26.37 37.86 23.49L37.86 23.49Q37.86 20.59 36.67 18.98Q35.49 17.36 33.09 17.36L33.09 17.36Q31.87 17.36 30.98 17.78Q30.08 18.19 29.48 18.98Q28.88 19.76 28.58 20.90Q28.29 22.05 28.29 23.49L28.29 23.49Q28.29 24.93 28.58 26.06Q28.88 27.20 29.48 27.98Q30.08 28.75 30.98 29.17Q31.87 29.58 33.09 29.58ZM47.17 31.58L44.32 31.58L44.32 15.38L45.95 15.38Q46.42 15.38 46.59 15.55Q46.77 15.73 46.83 16.16L46.83 16.16L47.02 18.69Q47.86 16.99 49.08 16.04Q50.30 15.09 51.95 15.09L51.95 15.09Q52.62 15.09 53.17 15.24Q53.71 15.39 54.18 15.66L54.18 15.66L53.81 17.79Q53.70 18.19 53.31 18.19L53.31 18.19Q53.09 18.19 52.62 18.04Q52.16 17.89 51.33 17.89L51.33 17.89Q49.84 17.89 48.84 18.75Q47.84 19.62 47.17 21.26L47.17 21.26L47.17 31.58ZM60.06 31.58L57.22 31.58L57.22 15.38L58.91 15.38Q59.52 15.38 59.68 15.97L59.68 15.97L59.89 17.63Q60.78 16.53 61.90 15.82Q63.01 15.12 64.48 15.12L64.48 15.12Q66.13 15.12 67.14 16.03Q68.16 16.94 68.61 18.50L68.61 18.50Q68.94 17.62 69.50 16.98Q70.05 16.34 70.74 15.92Q71.42 15.50 72.20 15.31Q72.98 15.12 73.78 15.12L73.78 15.12Q75.06 15.12 76.06 15.53Q77.06 15.94 77.75 16.72Q78.45 17.50 78.82 18.65Q79.18 19.79 79.18 21.26L79.18 21.26L79.18 31.58L76.34 31.58L76.34 21.26Q76.34 19.36 75.50 18.38Q74.67 17.39 73.09 17.39L73.09 17.39Q72.38 17.39 71.75 17.64Q71.12 17.89 70.64 18.37Q70.16 18.85 69.88 19.58Q69.60 20.30 69.60 21.26L69.60 21.26L69.60 31.58L66.75 31.58L66.75 21.26Q66.75 19.31 65.97 18.35Q65.18 17.39 63.68 17.39L63.68 17.39Q62.62 17.39 61.72 17.96Q60.82 18.53 60.06 19.50L60.06 19.50L60.06 31.58ZM96.43 10.77L95.68 12.24Q95.54 12.48 95.38 12.60Q95.22 12.72 94.96 12.72L94.96 12.72Q94.69 12.72 94.33 12.45Q93.97 12.18 93.42 11.85Q92.86 11.52 92.09 11.25Q91.31 10.98 90.21 10.98L90.21 10.98Q89.17 10.98 88.37 11.26Q87.57 11.54 87.03 12.02Q86.50 12.50 86.22 13.14Q85.95 13.79 85.95 14.54L85.95 14.54Q85.95 15.50 86.42 16.14Q86.90 16.77 87.67 17.22Q88.45 17.66 89.43 17.99Q90.42 18.32 91.45 18.67Q92.48 19.02 93.46 19.46Q94.45 19.90 95.22 20.58Q96 21.25 96.47 22.22Q96.94 23.20 96.94 24.62L96.94 24.62Q96.94 26.13 96.43 27.45Q95.92 28.77 94.94 29.74Q93.95 30.72 92.52 31.28Q91.09 31.84 89.26 31.84L89.26 31.84Q87.04 31.84 85.21 31.03Q83.38 30.22 82.08 28.85L82.08 28.85L82.98 27.38Q83.10 27.20 83.29 27.08Q83.47 26.96 83.70 26.96L83.70 26.96Q84.03 26.96 84.46 27.32Q84.90 27.68 85.54 28.11Q86.19 28.54 87.11 28.90Q88.03 29.26 89.36 29.26L89.36 29.26Q90.46 29.26 91.33 28.96Q92.19 28.66 92.79 28.10Q93.39 27.55 93.71 26.78Q94.03 26.02 94.03 25.07L94.03 25.07Q94.03 24.03 93.56 23.37Q93.09 22.70 92.32 22.26Q91.55 21.81 90.57 21.50Q89.58 21.18 88.55 20.86Q87.52 20.53 86.54 20.10Q85.55 19.66 84.78 18.98Q84.02 18.29 83.54 17.26Q83.07 16.22 83.07 14.70L83.07 14.70Q83.07 13.49 83.54 12.35Q84.02 11.22 84.91 10.34Q85.81 9.46 87.13 8.93Q88.45 8.40 90.16 8.40L90.16 8.40Q92.08 8.40 93.66 9.01Q95.25 9.62 96.43 10.77L96.43 10.77ZM103.30 31.58L100.45 31.58L100.45 8.02L103.30 8.02L103.30 17.55Q104.34 16.45 105.60 15.78Q106.86 15.12 108.51 15.12L108.51 15.12Q109.84 15.12 110.86 15.56Q111.87 16 112.55 16.81Q113.23 17.62 113.58 18.75Q113.94 19.89 113.94 21.26L113.94 21.26L113.94 31.58L111.09 31.58L111.09 21.26Q111.09 19.42 110.25 18.41Q109.41 17.39 107.68 17.39L107.68 17.39Q106.42 17.39 105.32 18Q104.22 18.61 103.30 19.65L103.30 19.65L103.30 31.58ZM118.56 15.38L121.41 15.38L121.41 31.58L118.56 31.58L118.56 15.38ZM122.05 10.29L122.05 10.29Q122.05 10.70 121.88 11.06Q121.71 11.42 121.43 11.70Q121.15 11.98 120.78 12.14Q120.42 12.30 120 12.30L120 12.30Q119.58 12.30 119.22 12.14Q118.86 11.98 118.58 11.70Q118.30 11.42 118.14 11.06Q117.98 10.70 117.98 10.29L117.98 10.29Q117.98 9.87 118.14 9.50Q118.30 9.12 118.58 8.84Q118.86 8.56 119.22 8.40Q119.58 8.24 120 8.24L120 8.24Q120.42 8.24 120.78 8.40Q121.15 8.56 121.43 8.84Q121.71 9.12 121.88 9.50Q122.05 9.87 122.05 10.29ZM129.94 31.58L127.07 31.58L127.07 17.81L125.28 17.60Q124.94 17.52 124.73 17.35Q124.51 17.18 124.51 16.86L124.51 16.86L124.51 15.70L127.07 15.70L127.07 14.13Q127.07 12.74 127.46 11.66Q127.86 10.58 128.58 9.83Q129.31 9.09 130.34 8.70Q131.36 8.32 132.64 8.32L132.64 8.32Q133.73 8.32 134.66 8.64L134.66 8.64L134.59 10.06Q134.58 10.38 134.32 10.45Q134.06 10.51 133.60 10.51L133.60 10.51L133.10 10.51Q132.37 10.51 131.77 10.70Q131.17 10.90 130.74 11.33Q130.30 11.76 130.07 12.46Q129.84 13.17 129.84 14.21L129.84 14.21L129.84 15.70L134.53 15.70L134.53 17.76L129.94 17.76L129.94 31.58ZM142.13 31.84L142.13 31.84Q140.21 31.84 139.18 30.77Q138.14 29.70 138.14 27.68L138.14 27.68L138.14 17.76L136.19 17.76Q135.94 17.76 135.76 17.61Q135.58 17.46 135.58 17.14L135.58 17.14L135.58 16L138.24 15.66L138.90 10.66Q138.93 10.42 139.10 10.26Q139.28 10.11 139.55 10.11L139.55 10.11L140.99 10.11L140.99 15.70L145.63 15.70L145.63 17.76L140.99 17.76L140.99 27.49Q140.99 28.51 141.49 29.01Q141.98 29.50 142.77 29.50L142.77 29.50Q143.22 29.50 143.54 29.38Q143.87 29.26 144.11 29.12Q144.35 28.98 144.52 28.86Q144.69 28.74 144.82 28.74L144.82 28.74Q145.04 28.74 145.22 29.01L145.22 29.01L146.05 30.37Q145.31 31.06 144.27 31.45Q143.23 31.84 142.13 31.84Z" fill="currentColor" />
          </svg>
        </Link>

        {/* Desktop links - only above 900px */}
        <nav className="hidden nav-desktop items-center gap-6 lg:gap-8 flex-1 justify-center">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              className="nav-link font-mono text-[12px] tracking-[0.18em] uppercase whitespace-nowrap"
              data-testid={`nav-${l.id}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={toggle}
            className="theme-toggle"
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            data-testid="theme-toggle"
          >
            <AnimatePresence mode="wait" initial={false}>
              {theme === "dark" ? (
                <motion.span
                  key="moon"
                  initial={{ opacity: 0, rotate: -30, scale: 0.6 }}
                  animate={{ opacity: 1, rotate: 0, scale: 1 }}
                  exit={{ opacity: 0, rotate: 30, scale: 0.6 }}
                  transition={{ duration: 0.25 }}
                  className="inline-flex"
                >
                  <Sun size={16} strokeWidth={1.6} />
                </motion.span>
              ) : (
                <motion.span
                  key="sun"
                  initial={{ opacity: 0, rotate: 30, scale: 0.6 }}
                  animate={{ opacity: 1, rotate: 0, scale: 1 }}
                  exit={{ opacity: 0, rotate: -30, scale: 0.6 }}
                  transition={{ duration: 0.25 }}
                  className="inline-flex"
                >
                  <Moon size={16} strokeWidth={1.6} />
                </motion.span>
              )}
            </AnimatePresence>
          </button>

          <a href="/#demo" className="cta hidden nav-desktop-cta" data-testid={IDS.navRequestDemo}>
            Request demo
            <span aria-hidden>{"\u2192"}</span>
          </a>

          {/* Mobile burger - visible below 900px */}
          <button
            className="nav-burger inline-flex items-center justify-center w-10 h-10 rounded-md border transition-colors"
            style={{ borderColor: "var(--hairline)" }}
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-burger"
          >
            <span className="relative w-4 h-3 block" aria-hidden>
              <span className={`absolute inset-x-0 top-0 h-px transition-transform duration-300 ${open ? "translate-y-[6px] rotate-45" : ""}`} style={{ background: "var(--text)" }} />
              <span className={`absolute inset-x-0 top-1/2 -translate-y-1/2 h-px transition-opacity duration-200 ${open ? "opacity-0" : "opacity-100"}`} style={{ background: "var(--text)" }} />
              <span className={`absolute inset-x-0 bottom-0 h-px transition-transform duration-300 ${open ? "-translate-y-[6px] -rotate-45" : ""}`} style={{ background: "var(--text)" }} />
            </span>
          </button>
        </div>
      </div>

      {/* Mobile overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="mobilenav"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 top-[64px] md:top-[68px] backdrop-blur-md z-30 nav-mobile-panel"
            style={{ background: "color-mix(in oklab, var(--bg) 92%, transparent)" }}
          >
            <div className="px-5 pt-8 pb-10 flex flex-col gap-1">
              {links.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.06 + i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                  className="block py-4 border-b border-white/8 font-display text-[26px] tracking-[-0.02em]"
                  data-testid={`nav-${l.id}-mobile`}
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.a
                href="/#demo"
                onClick={() => setOpen(false)}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.06 + links.length * 0.04 }}
                className="cta mt-8 self-start"
                data-testid="nav-request-demo-mobile"
              >
                Request demo
                <span aria-hidden>{"\u2192"}</span>
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
